// src/components/auth/EmailVerificationPending.js
import React, { useState } from 'react';
import { Mail, RefreshCw, CheckCircle, MessageCircle } from 'lucide-react';

const EmailVerificationPending = ({ email, onResendEmail, api }) => {
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendMessage('');
    setResendSuccess(false);

    try {
const response = await api.resendVerificationEmail(email);

      if (response.success) {
        setResendSuccess(true);
        setResendMessage('Verification email sent successfully!');
        if (onResendEmail) {
          onResendEmail();
        }
      }
    } catch (error) {
      setResendMessage(error.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl mb-4 shadow-lg">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">TicketDesk</h1>
          <p className="text-gray-600 mt-2">Customer Support Platform</p>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
            <Mail className="w-10 h-10 text-purple-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Check your email
          </h2>

          <p className="text-gray-600 mb-6">
            We've sent a verification link to:
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="font-medium text-gray-900">{email}</p>
          </div>

          <p className="text-sm text-gray-600 mb-8">
            Click the link in the email to verify your account and access TicketDesk. 
            The link will expire in 24 hours.
          </p>

          {/* Resend Section */}
          <div className="border-t border-gray-200 pt-6">
            <p className="text-sm text-gray-600 mb-4">
              Didn't receive the email?
            </p>

            {resendMessage && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                resendSuccess 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                <div className="flex items-center">
                  {resendSuccess ? (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  ) : (
                    <div className="w-4 h-4 mr-2" />
                  )}
                  {resendMessage}
                </div>
              </div>
            )}

            <button
              onClick={handleResendEmail}
              disabled={isResending || resendSuccess}
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isResending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend verification email
                </>
              )}
            </button>
          </div>

          {/* Help Section */}
          <div className="mt-8 text-xs text-gray-500">
            <p>
              Still having trouble? Check your spam folder or contact{' '}
              <a href="mailto:support@ticketdesk.app" className="text-purple-600 hover:text-purple-700">
                support@ticketdesk.app
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPending;